import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks';
import { authApi } from '@/api';
import { ApiError } from '@/api/client';
import {
  BusyButton,
  CardIntro,
  EmailChip,
  FieldError,
  FormAlert,
  StatusMark,
  StepProgress,
  backCls,
  emailMsg,
  linkCls,
  mmss,
  useCountdown,
} from '@/components/auth-kit';

const schema = z.object({
  email: z.string().superRefine((v, ctx) => {
    const m = emailMsg(v);
    if (m) ctx.addIssue({ code: 'custom', message: m });
  }),
});
type FormData = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [alert, setAlert] = useState<string | null>(null);
  const countdown = useCountdown();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setFocus,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { email: '' },
  });

  useEffect(() => {
    document.title = `${sentTo ? 'Check your inbox' : 'Reset password'} · authrz.me`;
  }, [sentTo]);

  const send = async (email: string, isResend = false) => {
    setAlert(null);
    setIsLoading(true);
    try {
      await authApi.forgotPassword({ email });
      setSentTo(email.toLowerCase());
      countdown.start(30);
      if (isResend) toast({ title: 'New link sent', description: email.toLowerCase() });
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        const msg = error.message || 'Please wait before requesting another reset.';
        if (isResend) {
          toast({ variant: 'destructive', title: 'Too many requests', description: msg });
          countdown.start(30);
        } else setAlert(msg);
      } else if (error instanceof ApiError && error.status === 400) {
        setAlert('Enter a valid email address.');
      } else if (error instanceof ApiError && error.status > 0 && error.status < 500) {
        // Do not reveal whether the account exists.
        setSentTo(email.toLowerCase());
        countdown.start(30);
      } else {
        setAlert('Couldn’t reach authrz.me. Check your connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = (data: FormData) => send(data.email.trim());

  if (sentTo) {
    return (
      <section className="flex flex-col gap-6" aria-labelledby="t2" aria-live="polite">
        <button
          type="button"
          className={backCls}
          onClick={() => {
            countdown.stop();
            setSentTo(null);
            window.setTimeout(() => setFocus('email'), 0);
          }}
        >
          <ArrowLeft />
          Use a different email
        </button>
        <StepProgress step={2} />
        <CardIntro
          id="t2"
          mark={
            <StatusMark tone="ok">
              <Mail />
            </StatusMark>
          }
          title="Check your inbox"
          sub={
            <>
              If an account exists for <EmailChip>{sentTo}</EmailChip>, a reset link is on its way.
              It works once and expires after 1 hour.
            </>
          }
        />
        <div className="flex items-center justify-between gap-3 text-[13px] text-muted-foreground">
          <span aria-live="polite">
            {countdown.left > 0 ? (
              <>
                Resend available in{' '}
                <span className="tabular-nums text-foreground">{mmss(countdown.left)}</span>
              </>
            ) : (
              'Didn’t get it? Check spam, or resend.'
            )}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={countdown.left > 0 || isLoading}
            onClick={() => send(sentTo, true)}
          >
            Resend link
          </Button>
        </div>
        <Button asChild size="lg" className="w-full">
          <Link to="/login">Back to sign in</Link>
        </Button>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6" aria-labelledby="t1">
      <Link to="/login" className={backCls}>
        <ArrowLeft />
        Back to sign in
      </Link>
      <StepProgress step={1} />
      <CardIntro
        id="t1"
        title="Forgot your password?"
        sub="Enter your account email and we’ll send a link to reset it."
      />
      {alert && <FormAlert title="Couldn’t send the reset link">{alert}</FormAlert>}
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@company.com"
            autoComplete="username"
            autoFocus
            className="h-11"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-err' : undefined}
            {...register('email')}
          />
          <FieldError id="email-err">{errors.email?.message}</FieldError>
        </div>
        <BusyButton type="submit" busy={isLoading} busyLabel="Sending link…">
          Send reset link
        </BusyButton>
      </form>
      <p className="text-center text-[13.5px] text-muted-foreground">
        Remembered it?{' '}
        <Link to="/login" className={linkCls}>
          Sign in
        </Link>
      </p>
    </section>
  );
}
